import React from "react";
import { Box, Typography } from "@mui/material";

import { tokens } from "../../theme/galleryTheme";

/** Text link with an arrow, used for "View all …" actions. Large enough to tap (44px). */
export function ArrowLink({ href, children, sx, ...rest }) {
  return (
    <Box
      component="a"
      href={href}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 1,
        minHeight: 44,
        color: tokens.color.ink,
        fontSize: "0.9375rem",
        fontWeight: 600,
        letterSpacing: "0.04em",
        textDecoration: "none",
        borderBottom: "1px solid transparent",
        whiteSpace: "nowrap",
        "& .lg-arrow": { transition: "transform 0.2s ease" },
        "&:hover": { textDecoration: "underline", textUnderlineOffset: "4px" },
        "&:hover .lg-arrow": { transform: "translateX(3px)" },
        ...sx,
      }}
      {...rest}
    >
      {children}
      <span className="lg-arrow" aria-hidden="true">
        →
      </span>
    </Box>
  );
}

/**
 * Section heading block: optional eyebrow label, serif title, optional intro text,
 * and an optional "View all" link aligned to the right on larger screens.
 */
export default function SectionHeader({
  id,
  eyebrow,
  title,
  description,
  actionLabel,
  actionHref,
  headingComponent = "h2",
  sx,
}) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        alignItems: { xs: "flex-start", md: "flex-end" },
        justifyContent: "space-between",
        gap: { xs: 1.5, md: 4 },
        mb: { xs: 4, md: 5 },
        pb: { xs: 2.5, md: 3 },
        borderBottom: `1px solid ${tokens.color.line}`,
        ...sx,
      }}
    >
      <Box sx={{ display: "grid", gap: 1, maxWidth: 760 }}>
        {eyebrow && (
          <Typography variant="overline" component="p">
            {eyebrow}
          </Typography>
        )}
        <Typography id={id} variant="h2" component={headingComponent}>
          {title}
        </Typography>
        {description && (
          <Typography variant="body1" sx={{ color: tokens.color.secondary, maxWidth: "62ch" }}>
            {description}
          </Typography>
        )}
      </Box>

      {actionHref && <ArrowLink href={actionHref}>{actionLabel}</ArrowLink>}
    </Box>
  );
}
